/**
 * FunnelStudio - Modern Funnel Analysis
 * 
 * A clean, intuitive workspace for creating and analyzing conversion funnels.
 * Inspired by Dub.sh's elegant flowing funnel visualization.
 * 
 * Key UX principles:
 * - Real-time updates: Analysis runs automatically when steps change
 * - Clean, minimal design: Matches dashboard patterns
 * - Vertical stacked layout for better flow
 * - Uses shadcn components throughout
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useOutletContext, useSearchParams, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LoadingState } from "@/components/shared/layout";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { formatNumeric } from "@/lib/utils/stringUtils";
import { safeRatio } from "@/lib/utils/stats";
import { parsePeriodToDateRange } from "@/lib/utils/heatmapUtils";
import {
  FUNNEL_COUNT_TOOLTIP,
  METRIC,
} from "@/lib/constants/metricsCopy";

// Hooks
import { useSavedFunnels, useSaveFunnel, useUpdateFunnel, useDeleteFunnel } from "@/hooks/useAnalytics";
import {
  useRunFunnelAnalysis,
  useFunnelSuggestions,
  useMultiPropertySuggestions,
  useStepMatchedTotals,
  type StepMatchQuery,
} from "@/hooks/useFunnelAnalysis";
import { useFormsBreakdown, useMultiPropertyFormsBreakdown } from "@/hooks/useFormsBreakdown";
import { buildSuggestedFunnels, isSuggestedFunnelId, type SuggestedFunnel } from "@/components/funnels/suggestedFunnels";
import { elementName, isGenericTarget } from "@/components/heatmaps/heatmapInsights";
import { useWorkspaceSafe } from "@/lib/contexts/WorkspaceContext";
import { usePlanUsage, useInvalidatePlanUsage } from "@/hooks/usePlanUsage";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";

// Components
import { FunnelFlowChart } from "@/components/funnels/FunnelFlowChart";
import { FunnelChartView, type FunnelChartStep, type FunnelChartStyle } from "@/components/funnels/FunnelChartViews";
import { PlanLimitBanner, CreateOrUpgradeButton, buildPlanLimitCopy } from "@/components/shared/plan-usage";
import { getPlanDisplayName } from "@/lib/constants/pricing";

// Types
import type { FunnelRequestParams, FunnelMode, FunnelSuggestionsResponse, StepCondition } from "@/lib/types/funnel-api";
import type { FormsBreakdownResponse } from "@/lib/api/external-api";
import { getTimezone, getGranularityForPeriod } from "@/lib/api/external-api";
import type { FilterClause } from "@/lib/routing/types";
import { FeatureGate } from "@/components/shared/FeatureGate";
import { ShellHeaderActions, ShellHeaderCrumb } from "@/components/layout/ShellHeader";
import { FunnelLibraryView, FunnelLibraryEmpty, type FunnelLibraryTile, type FunnelLibraryRow } from "@/components/funnels/FunnelLibrary";
import {
  formatRangeLabel,
  formatShortDate,
  formatSignedPercent,
  funnelChips,
  pickFunnelAnswer,
  savedFunnelUpdatedAt,
} from "@/components/funnels/funnelListModel";
import { useSavedFunnelResults } from "@/hooks/useSavedFunnelResults";
import { usePreviousPeriodMetrics, computePeriodDelta } from "@/hooks/usePreviousPeriodMetrics";
import { useURLState } from "@/hooks/useURLState";
import { useAuth } from "@/lib/contexts/AuthContext";
import { getPeriodUniqueVisitorTotal } from "@/lib/calculations/breakdownTotals";
import { logFunnelEvent, reportFunnelFailure } from "@/lib/observability/funnels";
import { HugeiconsIcon } from "@hugeicons/react"
import {
  Add01Icon,
  File01Icon,
  Cursor01Icon,
  Note01Icon,
  Idea01Icon,
  ArrowUpRight01Icon,
  InformationCircleIcon,
  Loading01Icon,
} from "@hugeicons/core-free-icons"

interface DashboardContext {
  dashboardData: any;
  propertyId?: string;
  currentDomain?: string;
  currentSite?: { domain?: string; propertyId?: string };
  period?: string;
  filters?: FilterClause[];
}

// Results view mode (chart vs table)
type ResultsView = "chart" | "table";

// Step condition types
type StepType = "page_view" | "click" | "form_submission";

const STEP_TYPE_CONFIG: Record<
  StepType,
  {
    label: string;
    shortLabel: string;
    icon: typeof File01Icon;
    placeholder: string;
    hint: string;
    color: string;
    chipBorder: string;
  }
> = {
  page_view: {
    label: "Page",
    shortLabel: "Page",
    icon: File01Icon,
    placeholder: "e.g. /pricing, /blog/*",
    hint: "Visited this URL",
    color: "text-muted-foreground",
    chipBorder: "border-border/50",
  },
  click: {
    label: "Click",
    shortLabel: "Click",
    icon: Cursor01Icon,
    placeholder: "e.g. Pricing CTA, #signup-btn",
    hint: "Clicked a button or link",
    color: "text-muted-foreground",
    chipBorder: "border-warning/30",
  },
  form_submission: {
    label: "Form",
    shortLabel: "Form",
    icon: Note01Icon,
    placeholder: "e.g. signup-form, contact",
    hint: "Submitted a form",
    color: "text-muted-foreground",
    chipBorder: "border-success/30",
  },
};

const STEP_TYPES = Object.keys(STEP_TYPE_CONFIG) as StepType[];

// Extended step model with condition type
interface StudioStep {
  name: string;
  path: string; // For page_view: URL path. For events: target selector
  type: StepType;
  propertyId?: string; // Which property this step targets (undefined = primary)
  secondary?: string; // For click/form: the event's secondary identifier (element type, etc.) — not the page
  additionalTargets?: Array<{ value: string; secondary?: string }>; // Extra OR conditions (click/form only)
}

// Minimal shape we read off each funnel step result (the endpoint returns more).
// total_events = raw event count (e.g. form submissions); total_sessions = unique visitors.
type FunnelStepResultLite = { total_sessions: number; total_events: number };

// Suggestion item used in the dropdown
interface SuggestionItem {
  value: string;
  label: string;
  sessions: number;
  type: StepType;
  secondary?: string;
  // Pages a form was submitted on, busiest first. Context only — a form step matches the
  // target on any page until the API can scope an event condition to a page.
  pages?: string[];
}

// Max steps (vertical layout keeps long funnels legible past 8)
const STEP_CAP = 15;
const WILDCARD_EXAMPLES = ["/blog/*", "/pricing*", "*"];

// Render a path/pattern with the * wildcard tinted — the one meaningful accent (DESIGN.md).
function Pattern({ value }: { value: string }) {
  const parts = String(value).split("*");
  return (
    <>
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {p}
          {i < parts.length - 1 && <span className="font-semibold text-primary">*</span>}
        </React.Fragment>
      ))}
    </>
  );
}

// glob (* = any) → which page suggestions a typed pattern would match (for the live preview)
function globToRegex(pattern: string): RegExp | null {
  try {
    const esc = pattern.trim().replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*");
    return new RegExp("^" + esc + "$");
  } catch {
    return null;
  }
}
function matchPagesForPattern(pageSuggestions: SuggestionItem[], pattern: string) {
  const pat = (pattern || "").trim();
  if (!pat) return { pages: [] as SuggestionItem[], total: 0 };
  const re = globToRegex(pat);
  if (!re) return { pages: [] as SuggestionItem[], total: 0 };
  let hits = pageSuggestions.filter((p) => re.test(p.value));
  if (hits.length === 0 && !pat.includes("*")) hits = pageSuggestions.filter((p) => p.value.startsWith(pat));
  return { pages: hits, total: hits.reduce((s, p) => s + (p.sessions || 0), 0) };
}

// Step Condition Input with type selector and smart suggestions
interface StepConditionInputProps {
  step: StudioStep;
  onChange: (updates: Partial<StudioStep>) => void;
  suggestions: SuggestionItem[];
  onFocus?: () => void;
  onSearchChange?: (value: string) => void;
  isActive?: boolean;
}

function StepConditionInput({ step, onChange, suggestions, onFocus, onSearchChange, isActive }: StepConditionInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const initialDisplay = step.secondary ? `${step.path} → ${step.secondary}` : step.path;
  const [inputValue, setInputValue] = useState(initialDisplay);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputValueRef = useRef(initialDisplay);
  const pathUpdateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const isMultiSelect = true;

  // All currently selected targets (primary + additional)
  const allTargets = useMemo(() => {
    if (!step.path) return [];
    return [
      { value: step.path, secondary: step.secondary },
      ...(step.additionalTargets || []),
    ];
  }, [step.path, step.secondary, step.additionalTargets]);

  const isTargetSelected = (value: string, secondary?: string) =>
    allTargets.some(
      (t) => t.value === value && (t.secondary ?? undefined) === (secondary ?? undefined)
    );

  // Sync with external value (e.g. quick picks) only when not actively filtering
  useEffect(() => {
    if (!isFocused) {
      // For click/form steps with a secondary, mirror the dropdown display so both
      // identifiers stay visible (e.g. "Link → button").
      const displayValue = step.secondary ? `${step.path} → ${step.secondary}` : step.path;
      setInputValue(displayValue);
      inputValueRef.current = displayValue;
    }
  }, [step.path, step.secondary, isFocused]);

  // Cleanup debounce timer on unmount
  useEffect(() => () => clearTimeout(pathUpdateTimer.current), []);

  // Filter suggestions based on current type and input
  const filteredSuggestions = useMemo(() => {
    const typeSuggestions = suggestions.filter(s => s.type === step.type);
    if (!inputValue.trim()) return typeSuggestions.slice(0, 8);
    const search = inputValue.toLowerCase();
    return typeSuggestions
      .filter((s) =>
        s.value.toLowerCase().includes(search) ||
        s.label.toLowerCase().includes(search) ||
        (s.secondary && s.secondary.toLowerCase().includes(search)) ||
        (s.pages || []).some((p) => p.toLowerCase().includes(search))
      )
      .slice(0, 8);
  }, [inputValue, suggestions, step.type]);

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    inputValueRef.current = newValue;
    if (isActive) onSearchChange?.(newValue);
    if (isMultiSelect) return; // input is filter-only; step data changes only via checkbox
    // Debounce the parent path update so auto-analysis doesn't fire on every keystroke
    clearTimeout(pathUpdateTimer.current);
    pathUpdateTimer.current = setTimeout(() => {
      pathUpdateTimer.current = undefined;
      onChange({ path: newValue, secondary: undefined });
    }, 400);
  };

  const handleSelect = (value: string, secondary?: string) => {
    clearTimeout(pathUpdateTimer.current);
    pathUpdateTimer.current = undefined;
    if (!isMultiSelect) {
      // page_view: replace and close
      setInputValue(value);
      inputValueRef.current = value;
      onChange({ path: value, secondary, additionalTargets: undefined });
      setIsFocused(false);
    } else {
      // click/form: toggle selection, keep dropdown open
      const selected = isTargetSelected(value, secondary);
      if (selected) {
        const newAll = allTargets.filter(
          (t) => !(t.value === value && (t.secondary ?? undefined) === (secondary ?? undefined))
        );
        if (newAll.length === 0) {
          onChange({ path: "", secondary: undefined, additionalTargets: undefined });
        } else {
          onChange({
            path: newAll[0].value,
            secondary: newAll[0].secondary,
            additionalTargets: newAll.length > 1 ? newAll.slice(1) : undefined,
          });
        }
      } else {
        if (!step.path) {
          onChange({ path: value, secondary, additionalTargets: undefined });
        } else {
          onChange({
            additionalTargets: [...(step.additionalTargets || []), { value, secondary }],
          });
        }
      }
      // Clear filter after any toggle so full list stays visible
      setInputValue("");
      inputValueRef.current = "";
    }
  };

  const removeAdditionalTarget = (idx: number) => {
    const newExtra = (step.additionalTargets || []).filter((_, i) => i !== idx);
    onChange({ additionalTargets: newExtra.length ? newExtra : undefined });
  };

  // Close on click outside, flushing any pending debounced path update
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Only call onChange if there's a pending debounced update that hasn't fired yet
        if (pathUpdateTimer.current !== undefined) {
          clearTimeout(pathUpdateTimer.current);
          pathUpdateTimer.current = undefined;
          onChange({ path: inputValueRef.current, secondary: undefined });
        }
        setIsFocused(false);
        setShowHelp(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showSuggestions = isFocused && filteredSuggestions.length > 0;
  const config = STEP_TYPE_CONFIG[step.type];

  // Wildcard discoverability: live preview + Try chips while editing a page pattern only
  // (gated on focus so filled steps stay quiet — config + count).
  const pageSuggestions = useMemo(() => suggestions.filter((s) => s.type === "page_view"), [suggestions]);
  const preview =
    isFocused && step.type === "page_view" && inputValue.trim()
      ? matchPagesForPattern(pageSuggestions, inputValue)
      : null;
  const showTryChips = isFocused && step.type === "page_view";

  return (
    <div className="flex-1 min-w-0" ref={containerRef}>
      <div className="flex items-center gap-2">
        {/* Event type — always-visible segmented control (Page / Click / Form) */}
        <div
          role="radiogroup"
          aria-label="Step event type"
          className="inline-flex shrink-0 items-center rounded-lg bg-muted p-[3px]"
        >
          {STEP_TYPES.map((type) => {
            const cfg = STEP_TYPE_CONFIG[type];
            const selected = step.type === type;
            return (
              <Tooltip key={type}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => {
                      if (selected) return;
                      onChange({
                        type,
                        path: "",
                        secondary: undefined,
                        additionalTargets: undefined,
                      });
                      setInputValue("");
                    }}
                    className={cn(
                      "inline-flex h-[26px] items-center rounded-md px-2 text-xs leading-4 transition-colors",
                      selected
                        ? "bg-card font-medium text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_1px_2px_rgb(0_0_0/0.04)]"
                        : "text-foreground/60 hover:text-foreground",
                    )}
                  >
                    {cfg.shortLabel}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  {cfg.hint}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Value input with suggestions dropdown */}
        <div className="flex-1 relative min-w-0">
          <Input
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              // Clear filter so full list is visible when opening dropdown
              setInputValue("");
              onFocus?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && inputValue.includes("*") && inputValue.trim()) {
                e.preventDefault();
                handleSelect(inputValue.trim());
                setIsFocused(false);
              }
            }}
            placeholder={config.placeholder}
            className={cn(
              "h-9 rounded-lg bg-background pr-9 text-[13px] shadow-none transition-colors focus-visible:ring-muted",
              isActive && "border-foreground/20"
            )}
          />

          {/* Wildcard / pattern help */}
          <button
            type="button"
            title="Pattern & wildcard help"
            onClick={(e) => {
              e.stopPropagation();
              setShowHelp((o) => !o);
              setIsFocused(false);
            }}
            className={cn(
              "absolute right-1.5 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors",
              showHelp ? "bg-muted text-foreground" : "text-muted-foreground/70 hover:bg-muted/60 hover:text-foreground"
            )}
          >
            <HugeiconsIcon icon={Idea01Icon} size={13} />
          </button>

          {/* Help popover */}
          <AnimatePresence>
            {showHelp && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 top-full z-[120] mt-1 w-[340px] max-w-[92vw] overflow-hidden rounded-xl bg-popover shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_12px_24px_-8px_rgb(0_0_0/0.12)]"
              >
                <div className="border-b border-border/30 px-3.5 py-3">
                  <div className="flex items-center gap-2 text-[13px] font-semibold text-foreground">
                    <HugeiconsIcon icon={Idea01Icon} size={15} className="text-foreground/60" />
                    Use <span className="rounded-sm bg-muted px-1.5 font-mono font-semibold">*</span> to match many pages
                  </div>
                  <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                    A wildcard stands in for any text. Great for blog posts, locale prefixes, or dynamic IDs you don't want to list one by one.
                  </p>
                </div>
                <div className="flex flex-col gap-0.5 p-2">
                  {[
                    ["/pricing", "Exact page. Only /pricing counts."],
                    ["/blog/*", "Any page under blog: /blog/seo, /blog/ab-testing…"],
                    ["/products/*/buy", "Wildcard in the middle: /products/pro/buy."],
                    ["*", "Matches every page on the domain."],
                    ["/checkout*", "Prefix match: /checkout, /checkout-success."],
                  ].map(([code, desc]) => (
                    <div
                      key={code}
                      className="grid grid-cols-[110px_1fr] items-baseline gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted/40"
                    >
                      <code className="font-mono text-[11.5px] font-semibold text-foreground">
                        <Pattern value={code} />
                      </code>
                      <span className="text-[11px] leading-snug text-muted-foreground">{desc}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Suggestions dropdown */}
          <AnimatePresence>
            {showSuggestions && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute top-full left-0 right-0 z-[100] mt-1 min-w-[240px] overflow-hidden rounded-xl bg-popover shadow-[0_0_0_1px_rgb(9_9_11/0.08),0_12px_24px_-8px_rgb(0_0_0/0.12)]"
              >
                <div className="max-h-[220px] overflow-y-auto">
                  <div className="flex items-center justify-between gap-1.5 border-b border-border px-3 py-2 text-xs font-medium text-foreground/60">
                    <span className="flex items-center gap-1.5">
                      <HugeiconsIcon icon={config.icon} size={12} className="text-muted-foreground" />
                      {step.type === "page_view" ? "Pages" : step.type === "click" ? "Click targets" : "Forms"}
                    </span>
                    {isMultiSelect && (
                      <span className="font-normal text-foreground/45">Select several</span>
                    )}
                  </div>
                  {filteredSuggestions.map((item) => {
                    const displayPrimary = item.secondary || item.value;
                    const baseSub = item.secondary ? item.value : (item.label !== item.value ? item.label : undefined);
                    const pageContext = item.pages?.length ? formatPageContext(item.pages) : undefined;
                    const displaySub = [baseSub, pageContext].filter(Boolean).join(" · ") || undefined;
                    const selected = isMultiSelect && isTargetSelected(item.value, item.secondary);
                    return (
                    <button
                      key={`${item.value}::${item.secondary || ""}`}
                      type="button"
                      onClick={() => handleSelect(item.value, item.secondary)}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/60",
                        selected && "bg-muted/60"
                      )}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {isMultiSelect && (
                          <div className={cn(
                            "w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 transition-colors",
                            selected ? "border-foreground bg-foreground" : "border-input"
                          )}>
                            {selected && (
                              <svg className="w-2 h-2 text-background" viewBox="0 0 8 8" fill="none">
                                <path d="M1.5 4l2 2L6.5 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <span className="block truncate text-[13px]">
                            {inputValue && displayPrimary.toLowerCase().includes(inputValue.toLowerCase()) ? (
                              <>
                                {displayPrimary.split(new RegExp(`(${inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i')).map((part, i) => (
                                  <span key={i} className={part.toLowerCase() === inputValue.toLowerCase() ? 'font-semibold' : ''}>
                                    {part}
                                  </span>
                                ))}
                              </>
                            ) : (
                              displayPrimary
                            )}
                          </span>
                          {displaySub && (
                            <span className="block truncate text-xs text-foreground/50">{displaySub}</span>
                          )}
                        </div>
                      </div>
                      {item.sessions > 0 && (
                        <span className="shrink-0 text-xs tabular-nums text-foreground/50">
                          {formatNumeric(item.sessions)}
                        </span>
                      )}
                    </button>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Live preview of what a typed page pattern matches (wildcard discoverability) */}
      {preview && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-1 text-[11px] text-muted-foreground">
          {preview.total > 0 ? (
            <>
              <svg className="size-3.5 text-foreground/60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              matches <span className="font-semibold tabular-nums text-foreground">{preview.pages.length}</span> page
              {preview.pages.length !== 1 ? "s" : ""} · <span className="font-semibold tabular-nums text-foreground">{formatNumeric(preview.total)}</span> unique visitors
              {preview.pages.slice(0, 2).map((p) => (
                <span key={p.value} className="rounded-sm bg-muted/50 px-1.5 py-px font-mono text-[11px]">
                  <Pattern value={p.value} />
                </span>
              ))}
              {preview.pages.length > 2 && <span className="text-[11px]">+{preview.pages.length - 2} more</span>}
            </>
          ) : (
            <span>
              <HugeiconsIcon icon={InformationCircleIcon} size={12} className="mr-1 inline -translate-y-px" />
              No pages match{" "}
              <span className="rounded-sm bg-muted/50 px-1.5 py-px font-mono text-[11px]"><Pattern value={inputValue.trim()} /></span> yet. Try a{" "}
              <span className="font-semibold text-foreground">*</span> wildcard.
            </span>
          )}
        </div>
      )}

      {/* "Try" example pattern chips (page type, when active) */}
      {showTryChips && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 pl-1">
          <span className="text-xs text-foreground/50">Try</span>
          {WILDCARD_EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => handleSelect(ex)}
              className="inline-flex h-6 items-center gap-1 rounded-md bg-card px-2 text-xs text-foreground/70 shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              <Pattern value={ex} />
            </button>
          ))}
        </div>
      )}

      {/* Additional target chips — explicit OR between conditions, wildcard tinted */}
      {step.additionalTargets && step.additionalTargets.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 pl-1">
          <span className="text-xs text-foreground/50">or</span>
          {step.additionalTargets.map((t, i) => (
            <span
              key={i}
              className="inline-flex h-6 items-center gap-1 rounded-md bg-muted px-2 text-xs text-foreground"
            >
              {t.secondary ? `${t.value} → ${t.secondary}` : t.value}
              <button
                type="button"
                onClick={() => removeAdditionalTarget(i)}
                className="leading-none text-muted-foreground transition-colors hover:text-destructive"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

    </div>
  );
}

// Where a form lives, keyed by form target. Highest-volume page first.
type FormPageIndex = Map<string, Array<{ page: string; submissions: number }>>;

// "on /signup" · "on /signup, /pricing" · "on /signup, /pricing +3"
function formatPageContext(pages: string[]): string {
  const shown = pages.slice(0, 2).join(", ");
  const rest = pages.length - 2;
  return `on ${shown}${rest > 0 ? ` +${rest}` : ""}`;
}

// Forms analytics reports some pages as absolute URLs; funnel targets are paths.
function toPagePath(raw?: string): string {
  const value = (raw || "").trim();
  if (!value) return "";
  const withoutOrigin = value.replace(/^https?:\/\/[^/]+/i, "");
  if (!withoutOrigin) return "/";
  return withoutOrigin.startsWith("/") ? withoutOrigin : `/${withoutOrigin}`;
}

/**
 * Index the page-first forms breakdown by form target, so a form suggestion can name the
 * pages it was submitted on.
 */
function buildFormPageIndex(formsResp?: FormsBreakdownResponse): FormPageIndex {
  const index: FormPageIndex = new Map();
  (formsResp?.data || []).forEach((row) => {
    const page = toPagePath(row.page);
    if (!page) return;
    (row.top_forms || []).forEach((form) => {
      if (!form.target) return;
      const pages = index.get(form.target) || [];
      pages.push({ page, submissions: form.submissions || 0 });
      index.set(form.target, pages);
    });
  });
  index.forEach((pages) => pages.sort((a, b) => b.submissions - a.submissions));
  return index;
}

/**
 * Build a SuggestionItem list from a suggestions API response.
 * Optionally augmented with dashboard page sessions (primary property only) and the forms
 * breakdown, which supplies the page context the suggestions endpoint omits for forms.
 */
function buildSuggestions(
  suggestionsResp: FunnelSuggestionsResponse | undefined,
  dashboardPages?: any[],
  formsResp?: FormsBreakdownResponse
): SuggestionItem[] {
  const items: SuggestionItem[] = [];
  const seenPaths = new Set<string>();

  if (dashboardPages) {
    dashboardPages.forEach((p: any) => {
      const path = p.page;
      if (path && !seenPaths.has(path)) {
        seenPaths.add(path);
        items.push({ value: path, label: path, sessions: p.sessions || p.count || 0, type: "page_view" });
      }
    });
  }

  (suggestionsResp?.data?.pages || []).forEach((p: any) => {
    const path = p.page || p.path;
    if (path && !seenPaths.has(path)) {
      seenPaths.add(path);
      items.push({ value: path, label: path, sessions: p.unique_sessions || p.sessions || 0, type: "page_view" });
    }
  });

  const seenClicks = new Set<string>();
  (suggestionsResp?.data?.clicks || []).forEach((c: any) => {
    const key = `${c.target}::${c.secondary || ""}`;
    if (seenClicks.has(key)) return;
    seenClicks.add(key);
    // "Link" alone names every link on the page; read the text, or say it has none.
    const named = isGenericTarget(c.target) ? elementName(c) ?? c.target : null;
    items.push({ value: c.target, label: named ?? (c.secondary ? `${c.target} (${c.secondary})` : c.target), sessions: c.unique_sessions || c.event_count || 0, type: "click", secondary: c.secondary || undefined });
  });

  // A form target alone ("Form") doesn't say where it lives, and the suggestions endpoint
  // carries no page for form events, so join the page-first forms breakdown to label each
  // target with the pages it was submitted on.
  const formPageIndex = buildFormPageIndex(formsResp);
  const seenForms = new Set<string>();
  (suggestionsResp?.data?.form_submissions || []).forEach((f: any) => {
    const key = `${f.target}::${f.secondary || ""}`;
    if (seenForms.has(key)) return;
    seenForms.add(key);
    items.push({
      value: f.target,
      label: f.secondary ? `${f.target} (${f.secondary})` : f.target,
      sessions: f.unique_sessions || f.event_count || 0,
      type: "form_submission",
      secondary: f.secondary || undefined,
      pages: formPageIndex.get(f.target)?.map((p) => p.page),
    });
  });

  return items;
}

// Saved funnels come from a couple of API shapes — normalize the id lookup in one place.
function getFunnelId(funnel: any): string | undefined {
  return funnel?.funnel_id || funnel?.funnelId || funnel?._id || funnel?.id;
}

const DEFAULT_FUNNEL_NAME = "Untitled funnel";

function FunnelStudioInner() {
  const context = useOutletContext<any>();
  const params = useParams<{ domain?: string }>();
  
  // Extract what we need - the context structure may vary
  const propertyId = context?.propertyId;
  const dashboardData = context?.dashboardData;
  // Get period from outlet context (provided by FilterBar/FilterContext)
  const period = context?.period || "LAST_30_DAYS";

  // Derive start/end from period so suggestions reflect the selected date range
  const suggestionDateRange = useMemo(() => {
    let startDate: Date;
    let endDate: Date = new Date();
    if (period.startsWith("CUSTOM_RANGE:")) {
      const parts = period.replace("CUSTOM_RANGE:", "").split("_to_");
      if (parts.length === 2) {
        startDate = new Date(parts[0]);
        endDate = new Date(parts[1]);
        endDate.setHours(23, 59, 59, 999);
      } else {
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      }
    } else {
      const dateRange = parsePeriodToDateRange(period);
      startDate = dateRange?.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      endDate = dateRange?.endDate || new Date();
    }
    return {
      start: startDate.toISOString(),
      end: endDate.toISOString(),
      timezone: getTimezone(),
      granularity: getGranularityForPeriod(period),
    };
  }, [period]);

  // Global FilterBar chips (utm/campaign/device/country). These get AND-merged
  // into every step's conditions so the funnel respects the same scope as the
  // rest of the workspace (e.g. Campaign: humb_sales_coldpro filters all steps).
  const activeFilters = context?.filters ?? [];
  type FunnelFilterFields = Pick<
    StepCondition,
    | "utm_source" | "utm_medium" | "utm_campaign" | "utm_content" | "utm_id" | "utm_term"
    | "device" | "os" | "browser" | "referrer" | "region" | "country"
  >;
  const filterFields = useMemo<FunnelFilterFields>(() => {
    const out: FunnelFilterFields = {};
    for (const f of activeFilters) {
      if (f.operator !== ":") continue;

      switch (f.field) {
        case "utm_source":    out.utm_source = f.value; break;
        case "utm_campaign":  out.utm_campaign = f.value; break;
        case "utm_medium":    out.utm_medium = f.value; break;
        case "utm_content":   out.utm_content = f.value; break;
        case "utm_id":        out.utm_id = f.value; break;
        case "utm_term":      out.utm_term = f.value; break;
        case "device_type":
        case "device":        out.device = f.value; break;
        case "os":            out.os = f.value; break;
        case "browser":       out.browser = f.value; break;
        case "referrer":      out.referrer = f.value; break;
        case "region":        out.region = f.value; break;
        case "country":       out.country = f.value; break;
        // "page", "entry_page", "exit_page" have no per-step equivalent — skip
      }
    }
    return out;
  }, [activeFilters]);
  // Stable signature for effect deps (object identity flips on every render otherwise)
  const filterFieldsKey = useMemo(
    () => JSON.stringify(filterFields),
    [filterFields]
  );
  
  // Get domain from URL params (most reliable) or fallback to context
  const domain = 
    params?.domain ||
    context?.property?.domain ||  // workspace dashboard layout
    context?.currentDomain || 
    context?.currentSite?.domain ||
    dashboardData?.currentDomain ||
    dashboardData?.currentSite?.domain;
  
  const [searchParams, setSearchParams] = useSearchParams();

  const observabilityScope = useMemo(() => ({ propertyId, domain }), [propertyId, domain]);

  // Workspace properties for cross-domain step selection
  const workspace = useWorkspaceSafe();
  const enabledProperties = workspace?.enabledProperties || [];

  // Core state
  const [funnelName, setFunnelName] = useState(DEFAULT_FUNNEL_NAME);
  const [steps, setSteps] = useState<StudioStep[]>([
    { name: "Step 1", path: "", type: "page_view" },
    { name: "Step 2", path: "", type: "page_view" },
  ]);
  const [activeStepIndex, setActiveStepIndex] = useState<number | null>(0);
  const [mode, setMode] = useState<FunnelMode>("sequential");
  const [resultsView, setResultsView] = useState<ResultsView>("chart");
  const [chartStyle, setChartStyle] = useState<FunnelChartStyle>("steps");
  const [showLibrary, setShowLibrary] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // The saved funnel currently loaded for editing (null = building a brand-new, unsaved funnel).
  // When set, Save calls the update endpoint instead of create — otherwise re-saving an edited
  // funnel would create a duplicate (or fail with a 402 plan-limit error, silently dropping edits).
  const [editingFunnelId, setEditingFunnelId] = useState<string | null>(null);
  // Guards the URL → funnel restore-on-refresh effect so it only runs once per mount.
  const restoredFromUrlRef = useRef(false);

  // Search state for funnel suggestions — debounced before hitting the API
  const [activeSearch, setActiveSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showTotals, setShowTotals] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(activeSearch), 300);
    return () => clearTimeout(t);
  }, [activeSearch]);

  // API hooks
  const { data: savedFunnels, isLoading: funnelsLoading, refetch: refetchFunnels } = useSavedFunnels({ propertyId });
  const saveFunnelMutation = useSaveFunnel({ propertyId });
  const updateFunnelMutation = useUpdateFunnel({ propertyId });
  const deleteFunnelMutation = useDeleteFunnel({ propertyId });
  const [funnelToDelete, setFunnelToDelete] = useState<any | null>(null);

  // Plan-limit awareness — mirrors the heatmaps pattern. `canAdd` already
  // encodes trial bypass and the fallback path (see usePlanUsage), so it's
  // the single source of truth for gating "New Funnel" / AI suggestions /
  // the first save of a brand-new funnel. The locally loaded funnel count
  // feeds the fallback so the gate keeps working when the usage endpoint
  // can't answer.
  const { openPricingModal, currentPlan, planName } = useSubscription();
  const { funnels: funnelsUsage, metadata: planUsageMetadata } = usePlanUsage({
    propertyId,
    fallbackCounts: { funnels: savedFunnels?.funnels?.length },
  });
  const invalidatePlanUsage = useInvalidatePlanUsage();
  const displayPlanName = getPlanDisplayName(currentPlan, planUsageMetadata?.planLabel || planName);
  const funnelLimitCopy = buildPlanLimitCopy({
    noun: "funnels",
    limit: funnelsUsage.limit,
    planName: displayPlanName,
    resolution: "Delete one to make room, or move to Scale for unlimited funnels.",
  });
  const [funnelLimitBannerDismissed, setFunnelLimitBannerDismissed] = useState(false);
  useEffect(() => {
    if (funnelsUsage.canAdd && funnelLimitBannerDismissed) setFunnelLimitBannerDismissed(false);
  }, [funnelsUsage.canAdd, funnelLimitBannerDismissed]);
  const runFunnel = useRunFunnelAnalysis(propertyId);
  // Base suggestions — always fetched, never replaced by search (keeps Quick Picks stable)
  const { data: baseSuggestions } = useFunnelSuggestions(propertyId, suggestionDateRange);
  // Search-augmented suggestions — silent background fetch, only when typing
  const { data: searchSuggestionsData } = useFunnelSuggestions(
    debouncedSearch ? propertyId : undefined,
    debouncedSearch ? { search: debouncedSearch, ...suggestionDateRange } : undefined
  );

  // Collect unique non-primary propertyIds from current steps to fetch their suggestions
  const crossDomainPropertyIds = useMemo(() => {
    const ids = new Set<string>();
    steps.forEach((s) => {
      if (s.propertyId && s.propertyId !== propertyId) ids.add(s.propertyId);
    });
    return Array.from(ids);
  }, [steps, propertyId]);

  const multiPropertySuggestions = useMultiPropertySuggestions(crossDomainPropertyIds);

  // Page context for form targets — the suggestions endpoint doesn't carry it yet.
  const { data: formsBreakdown } = useFormsBreakdown(propertyId, suggestionDateRange);
  const multiPropertyFormsBreakdown = useMultiPropertyFormsBreakdown(
    crossDomainPropertyIds,
    suggestionDateRange
  );

  const funnels = useMemo(() => savedFunnels?.funnels || [], [savedFunnels]);

  // Funnels pre-built from this site's pages, clicks and forms, listed (and
  // run) beside the saved ones until there are three saved. Not AI: see
  // suggestedFunnels.ts. One that matches a saved funnel's steps is dropped.
  const suggestedFunnels = useMemo<SuggestedFunnel[]>(() => {
    if (funnels.length >= 3) return [];
    const savedSigs = new Set(
      funnels.map((f: any) => (f.steps || []).map((st: any) => `${st.type || "page_view"}:${st.url || st.path}`).join(">")),
    );
    return buildSuggestedFunnels(baseSuggestions?.data, 3 - Math.min(funnels.length, 2)).filter(
      (sf) => !savedSigs.has(sf.steps.map((st) => `${st.type}:${st.url}`).join(">")),
    );
  }, [funnels, baseSuggestions]);

  // Primary property suggestions — stable, based on base query only (no search dependency)
  const allSuggestions = useMemo(
    () => buildSuggestions(baseSuggestions, dashboardData?.metrics?.pageSessions, formsBreakdown),
    [dashboardData, baseSuggestions, formsBreakdown]
  );

  // Merged suggestions for the active step dropdown: base + any additional from search (deduplicated)
  const mergedActiveSuggestions = useMemo(() => {
    if (!debouncedSearch || !searchSuggestionsData) return allSuggestions;
    const seenKey = (s: SuggestionItem) => `${s.type}::${s.value}::${s.secondary || ""}`;
    const seen = new Set(allSuggestions.map(seenKey));
    const additional = buildSuggestions(searchSuggestionsData, undefined, formsBreakdown).filter(s => !seen.has(seenKey(s)));
    return [...allSuggestions, ...additional];
  }, [allSuggestions, searchSuggestionsData, debouncedSearch, formsBreakdown]);

  // Return suggestions scoped to a step's property (falls back to primary)
  const getSuggestionsForStep = useCallback(
    (stepPropertyId?: string, forActiveStep = false): SuggestionItem[] => {
      if (!stepPropertyId || stepPropertyId === propertyId) {
        return forActiveStep ? mergedActiveSuggestions : allSuggestions;
      }
      const resp = multiPropertySuggestions.get(stepPropertyId);
      return buildSuggestions(resp, undefined, multiPropertyFormsBreakdown.get(stepPropertyId));
    },
    [allSuggestions, mergedActiveSuggestions, multiPropertySuggestions, multiPropertyFormsBreakdown, propertyId]
  );

  // Resolve the active period string into an ISO date range.
  const getDateRange = useCallback((): { start: string; end: string } => {
    let startDate: Date;
    let endDate: Date = new Date();
    if (period.startsWith('CUSTOM_RANGE:')) {
      const parts = period.replace('CUSTOM_RANGE:', '').split('_to_');
      if (parts.length === 2) {
        startDate = new Date(parts[0]);
        endDate = new Date(parts[1]);
        endDate.setHours(23, 59, 59, 999); // End of day
      } else {
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      }
    } else {
      const dateRange = parsePeriodToDateRange(period);
      startDate = dateRange?.startDate || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      endDate = dateRange?.endDate || new Date();
    }
    return { start: startDate.toISOString(), end: endDate.toISOString() };
  }, [period]);

  // Build the API condition array for one step (page glob or event target, + OR targets).
  // FilterBar selections (utm/device/country) ride along on every condition (AND logic).
  const buildStepConditions = useCallback((s: StudioStep) => {
    if (s.type === "page_view") {
      const extra = (s.additionalTargets || []).map((t) => ({ type: "page_view" as const, path: t.value, ...filterFields }));
      return [{ type: "page_view" as const, path: s.path, ...filterFields }, ...extra];
    }
    const primary = { type: "event" as const, action: s.type as any, target: s.path, ...(s.secondary && { secondary: s.secondary }), ...filterFields };
    const extra = (s.additionalTargets || []).map((t) => ({
      type: "event" as const,
      action: s.type as any,
      target: t.value,
      ...(t.secondary && { secondary: t.secondary }),
      ...filterFields,
    }));
    return [primary, ...extra];
  }, [filterFields]);

  // Build params - convert period string to date range
  const buildParams = (): FunnelRequestParams => {
    const validSteps = steps.filter(s => s.path.trim());
    const { start, end } = getDateRange();
    return {
      start,
      end,
      mode,
      timezone: getTimezone(),
      granularity: getGranularityForPeriod(period),
      steps: validSteps.map((s) => {
        const stepPropertyId = s.propertyId && s.propertyId !== propertyId ? s.propertyId : undefined;
        return {
          name: s.name,
          ...(stepPropertyId && { property_id: stepPropertyId }),
          conditions: buildStepConditions(s),
        };
      }),
    };
  };

  // Auto-run analysis when steps change
  useEffect(() => {
    const validSteps = steps.filter(s => s.path.trim());
    if (validSteps.length >= 2 && propertyId && !showLibrary) {
      const timeout = setTimeout(() => {
        runFunnel.mutate(buildParams());
      }, 600);
      return () => clearTimeout(timeout);
    }
  }, [steps, period, mode, propertyId, showLibrary, filterFieldsKey]);

  // Step handlers
  const addStep = () => {
    if (steps.length >= STEP_CAP) return;
    const nextSteps = [...steps, { name: `Step ${steps.length + 1}`, path: "", type: "page_view" as StepType }];
    setSteps(nextSteps);
    setActiveStepIndex(nextSteps.length - 1);
  };

  const removeStep = (index: number) => {
    if (steps.length <= 2) return;
    const nextSteps = steps.filter((_, i) => i !== index);
    setSteps(nextSteps);
    setActiveStepIndex((prev) => {
      if (prev === null) return null;
      if (index === prev) {
        return Math.max(0, Math.min(prev - 1, nextSteps.length - 1));
      }
      if (prev > index) {
        return prev - 1;
      }
      return prev;
    });
  };

  const handleSuggestionApply = (path: string) => {
    if (!path) return;
    let targetIndex =
      typeof activeStepIndex === "number" ? activeStepIndex : steps.findIndex((step) => !step.path.trim());

    if (targetIndex === -1) {
      if (steps.length < STEP_CAP) {
        const nextSteps = [...steps, { name: `Step ${steps.length + 1}`, path, type: "page_view" as StepType }];
        setSteps(nextSteps);
        setActiveStepIndex(nextSteps.length - 1);
        return;
      }
      targetIndex = steps.length - 1;
    }

    updateStep(targetIndex, { path });
    setActiveStepIndex(targetIndex);
  };

  const updateStep = (index: number, updates: Partial<StudioStep>) => {
    setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)));
  };

  // Load AI-generated suggestion - load into studio and let auto-run handle it
  const loadSuggestedFunnel = (suggestion: SuggestedFunnel) => {
    if (!funnelsUsage.canAdd) {
      openPricingModal({ reason: "funnel_limit" });
      return;
    }
    const newSteps: StudioStep[] = suggestion.steps.map((st) => ({
      name: st.name,
      path: st.url,
      type: st.type,
      ...(st.secondary && { secondary: st.secondary }),
    }));

    setFunnelName(suggestion.name);
    setSteps(newSteps);
    setEditingFunnelId(null);
    setShowLibrary(false);
  };
  // Load saved funnel - load into studio and let auto-run handle it
  const loadFunnel = (funnel: any) => {
    const newSteps: StudioStep[] = (funnel.steps || []).map((s: any, i: number) => ({
      name: s.name || `Step ${i + 1}`,
      path: s.url || s.path || "",
      type: (s.type as StepType) || "page_view",
      ...(s.propertyId && { propertyId: s.propertyId }),
      ...(s.secondary && { secondary: s.secondary }),
      ...(s.additionalTargets?.length && { additionalTargets: s.additionalTargets }),
    }));

    setFunnelName(funnel.name);
    setSteps(newSteps);
    runFunnel.reset();
    setShowLibrary(false);
    setEditingFunnelId(getFunnelId(funnel) ?? null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      const id = getFunnelId(funnel);
      if (id) next.set("funnel", id);
      return next;
    });
  };

  // Restore the funnel being edited after a page refresh. `loadFunnel` sets `?funnel=<id>` in the
  // URL, but without this the URL param was never read back — a refresh always dropped the user
  // into the library view and lost every step that wasn't persisted yet.
  useEffect(() => {
    if (restoredFromUrlRef.current) return;
    if (funnelsLoading) return; // wait for the saved-funnels list before deciding
    restoredFromUrlRef.current = true;
    const funnelIdFromUrl = searchParams.get("funnel");
    if (!funnelIdFromUrl) return;
    const match = funnels.find((f: any) => getFunnelId(f) === funnelIdFromUrl);
    if (match) loadFunnel(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funnelsLoading, funnels]);

  // Save — updates the loaded funnel in place when editing an existing one, otherwise creates
  // a new one. This is the core fix: re-saving an edited funnel must NOT re-run create (which
  // either duplicates the funnel or, at the plan's funnel limit, fails with a 402 and silently
  // drops the edit — see SUPENG-149).
  const handleSave = async () => {
    if (!funnelName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    // Only persist steps the user has actually configured — skip empty placeholder rows.
    const stepsToSave = steps
      .filter((s) => s.path.trim())
      .map((s, i) => ({
        id: String(i + 1),
        name: s.name,
        url: s.path,
        type: s.type,
        ...(s.propertyId && { propertyId: s.propertyId }),
        ...(s.secondary && { secondary: s.secondary }),
        ...(s.additionalTargets?.length && { additionalTargets: s.additionalTargets }),
      }));
    if (stepsToSave.length < 2) {
      toast.error("Configure at least 2 steps before saving");
      return;
    }
    // Extra backstop beyond the handleNew/loadAISuggestion gates: a brand-new
    // (never-saved) funnel can still reach here if usage changed mid-edit.
    // Editing an already-saved funnel is never blocked — only new creates count.
    if (!editingFunnelId && !funnelsUsage.canAdd) {
      openPricingModal({ reason: "funnel_limit" });
      return;
    }
    const funnelData = { name: funnelName, description: "", steps: stepsToSave };

    setIsSaving(true);
    try {
      if (editingFunnelId) {
        await updateFunnelMutation.mutateAsync({ funnelId: editingFunnelId, funnelData });
        logFunnelEvent("funnel_updated", observabilityScope, {
          funnelId: editingFunnelId,
          funnelName,
          stepCount: stepsToSave.length,
        });
        toast.success("Funnel saved");
      } else {
        const created = await saveFunnelMutation.mutateAsync(funnelData);
        logFunnelEvent("funnel_saved", observabilityScope, {
          funnelName,
          stepCount: stepsToSave.length,
        });
        invalidatePlanUsage();
        toast.success("Funnel saved");
        // Capture the new funnel's id so the *next* Save updates it instead of creating another.
        // Fall back to matching by name against the freshly-refetched list if the create response
        // doesn't echo an id directly.
        let newId = getFunnelId(created) ?? getFunnelId(created?.funnel);
        if (!newId) {
          const refreshed = await refetchFunnels();
          const candidates = (refreshed.data?.funnels || []).filter((f: any) => f.name === funnelData.name);
          newId = getFunnelId(candidates[candidates.length - 1]);
        } else {
          refetchFunnels();
        }
        if (newId) {
          setEditingFunnelId(newId);
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("funnel", newId);
            return next;
          });
        }
      }
      if (editingFunnelId) refetchFunnels();
    } catch (err: any) {
      reportFunnelFailure(editingFunnelId ? "update" : "save", err, observabilityScope, {
        funnelId: editingFunnelId,
        funnelName,
        stepCount: stepsToSave.length,
      });

      if (err?.status === 402) {
        toast.error("Funnel limit reached", {
          description: "This is a plan limit, not an error. Your changes were NOT saved. Upgrade your plan or delete another funnel first.",
          action: {
            label: "Upgrade",
            onClick: () => openPricingModal({ reason: "funnel_limit" }),
          },
        });
      } else {
        toast.error(err.message || "Failed to save");
      }
    }
    setIsSaving(false);
  };

  // New funnel
  const handleNew = () => {
    setFunnelName(DEFAULT_FUNNEL_NAME);
    if (!funnelsUsage.canAdd) {
      openPricingModal({ reason: "funnel_limit" });
      return;
    }
    setSteps([
      { name: "Step 1", path: "", type: "page_view" },
      { name: "Step 2", path: "", type: "page_view" },
    ]);
    runFunnel.reset();
    setShowLibrary(false);
    setEditingFunnelId(null);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("funnel");
      return next;
    });
  };

  // Back to library — must also clear `?funnel=` from the URL. Otherwise a refresh while on the
  // library view re-triggers the restore-from-URL effect and bounces the user straight back into
  // the funnel they just backed out of.
  const handleBackToLibrary = () => {
    setShowLibrary(true);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete("funnel");
      return next;
    });
  };

  // Delete funnel
  const handleDeleteFunnel = async () => {
    if (!funnelToDelete) return;
    try {
      const deletedId = getFunnelId(funnelToDelete);
      await deleteFunnelMutation.mutateAsync(deletedId!);
      logFunnelEvent("funnel_deleted", observabilityScope, {
        funnelId: deletedId,
        funnelName: funnelToDelete.name,
      });
      invalidatePlanUsage();
      toast.success(`"${funnelToDelete.name}" has been deleted`);
      setFunnelToDelete(null);
      if (deletedId && deletedId === editingFunnelId) {
        // Currently-open funnel was deleted from the library list — don't leave the editor
        // pointed at an id that no longer exists.
        setEditingFunnelId(null);
      }
      refetchFunnels();
    } catch (err) {
      reportFunnelFailure("delete", err, observabilityScope, {
        funnelId: getFunnelId(funnelToDelete),
        funnelName: funnelToDelete.name,
      });
      toast.error("Failed to delete funnel");
    }
  };

  const hasValidSteps = steps.filter(s => s.path.trim()).length >= 2;
  const isLoading = runFunnel.isPending;
  const hasFlowResults = Boolean(runFunnel.data) && !runFunnel.isPending;
  const error = runFunnel.error;
  const errorMessage = error instanceof Error
    ? error.message
    : typeof error === "string"
    ? error
    : "Analysis failed. Please try again.";

  // ── Matched-vs-in-funnel derivations ──────────────────────────────────────
  // "in funnel" comes from the funnel results (arrived via the path, in order).
  // "matched total" = everyone who hit the step's target(s) any way. There's no count
  // endpoint, so we get the EXACT figure from the funnel engine itself — one tiny cached
  // query per step (see useStepMatchedTotals) — honoring the same wildcard / cross-domain
  // matching. off-path = matched − in funnel.
  const effProp = useCallback((s: StudioStep) => s.propertyId || propertyId, [propertyId]);
  const domainForStep = useCallback(
    (s: StudioStep): string | undefined => {
      const pid = effProp(s);
      const prop = enabledProperties.find((p: any) => p.propertyId === pid);
      return prop?.domain || (pid === propertyId ? domain : undefined);
    },
    [effProp, enabledProperties, propertyId, domain]
  );
  const matchKey = useCallback(
    (s: StudioStep) => `${effProp(s)}|${JSON.stringify(buildStepConditions(s))}`,
    [effProp, buildStepConditions]
  );
  // Stable per-period range so the matched-total cache isn't busted on every keystroke.
  const dateRange = useMemo(() => getDateRange(), [getDateRange]);
  // One exact matched-total query per configured step (deduped, cached, skipped in library view).
  const matchQueries: StepMatchQuery[] = useMemo(() => {
    if (showLibrary) return [];
    const { start, end } = dateRange;
    const timezone = getTimezone();
    const granularity = getGranularityForPeriod(period);
    const seen = new Set<string>();
    const out: StepMatchQuery[] = [];
    steps.forEach((s) => {
      const pid = effProp(s);
      if (!s.path.trim() || !pid) return;
      const key = matchKey(s);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ key, propertyId: pid, start, end, timezone, granularity, conditions: buildStepConditions(s) });
    });
    return out;
  }, [steps, showLibrary, dateRange, period, effProp, matchKey, buildStepConditions]);
  const matchedTotals = useStepMatchedTotals(matchQueries);
  // Suggestions-sum: undercounts wildcards / long-tail targets — only a fallback while
  // the exact query is in flight.
  const matchedFromSuggestions = useCallback(
    (s: StudioStep): number => {
      const sugg = getSuggestionsForStep(s.propertyId);
      const targets = [{ value: s.path, secondary: s.secondary }, ...(s.additionalTargets || [])];
      return targets.reduce((sum, t) => {
        const m = sugg.find(
          (x) => x.type === s.type && x.value === t.value && (x.secondary ?? undefined) === (t.secondary ?? undefined)
        );
        return sum + (m?.sessions || 0);
      }, 0);
    },
    [getSuggestionsForStep]
  );
  const matchedForStep = useCallback(
    (s: StudioStep): { sessions: number; events: number } => {
      if (!s.path.trim()) return { sessions: 0, events: 0 };
      const exact = matchedTotals.get(matchKey(s));
      if (exact) return exact;
      const sessions = matchedFromSuggestions(s);
      return { sessions, events: 0 };
    },
    [matchedTotals, matchKey, matchedFromSuggestions]
  );
  const targetLabelForStep = useCallback(
    (s: StudioStep): string => {
      if (s.type === "page_view") {
        const host = domainForStep(s);
        return host ? `${host}${s.path}` : s.path;
      }
      return s.secondary || s.path;
    },
    [domainForStep]
  );

  // Map original step index → funnel result (results align with the valid-steps order).
  const resultByStepIndex = useMemo(() => {
    const map = new Map<number, FunnelStepResultLite>();
    const results = (runFunnel.data?.data.steps || []) as FunnelStepResultLite[];
    let vi = 0;
    steps.forEach((s, i) => {
      if (s.path.trim()) {
        if (results[vi]) map.set(i, results[vi]);
        vi += 1;
      }
    });
    return map;
  }, [steps, runFunnel.data]);

  // Event type per valid step, aligned with runFunnel's result order — lets the Stream
  // chart label form steps as "Unique form submissions" instead of "visitors".
  const validStepTypes = useMemo(
    () => steps.filter((s) => s.path.trim()).map((s) => s.type),
    [steps]
  );

  // Steps shaped for the Stream/Bars/Vertical chart views.
  const chartSteps: FunnelChartStep[] = useMemo(() => {
    const valid = steps.filter((s) => s.path.trim());
    const results = (runFunnel.data?.data.steps || []) as FunnelStepResultLite[];
    return valid.map((s, i) => {
      const inFunnel = results[i]?.total_sessions ?? 0;
      const { sessions: matchedSessions, events: matchedEvents } = matchedForStep(s);
      const matched = Math.max(matchedSessions, inFunnel);
      const crosses = i > 0 && enabledProperties.length > 1 && effProp(s) !== effProp(valid[i - 1]);
      return {
        id: `${i}-${s.name}`,
        name: s.name || `Step ${i + 1}`,
        inFunnel,
        matches: matched,
        totalEvents: results[i]?.total_events,
        matchedEvents: matchedEvents > 0 ? Math.max(matchedEvents, results[i]?.total_events ?? 0) : undefined,
        targetLabel: targetLabelForStep(s),
        conditionCount: 1 + (s.additionalTargets?.length || 0),
        crosses,
        type: s.type,
      };
    });
  }, [steps, runFunnel.data, matchedForStep, targetLabelForStep, effProp, enabledProperties.length]);

  // Long funnels fall back to the vertical layout (stream/bars stay best ≤8 steps).
  const validStepCount = steps.filter((s) => s.path.trim()).length;
  const tooMany = validStepCount > 8;
  const effChartStyle: FunnelChartStyle = tooMany && chartStyle !== "steps" ? "list" : chartStyle;

  // Keep active step aligned with current steps
  useEffect(() => {
    if (steps.length === 0) {
      setActiveStepIndex(null);
      return;
    }
    setActiveStepIndex((prev) => {
      if (prev !== null && prev <= steps.length - 1) {
        return prev;
      }
      const firstEmpty = steps.findIndex((step) => !step.path.trim());
      if (firstEmpty !== -1) {
        return firstEmpty;
      }
      return steps.length - 1;
    });
  }, [steps]);

  // ── Library data ─────────────────────────────────────────────────────
  // Per-funnel conversion for the selected window, the previous-period
  // visitors delta and the tile figures. Hooks run unconditionally (the
  // library is an early return below).
  const funnelResults = useSavedFunnelResults({
    funnels,
    propertyId,
    window: suggestionDateRange,
    filterFields: filterFields as Record<string, string | undefined>,
    enabled: showLibrary,
  });
  const suggestedResults = useSavedFunnelResults({
    funnels: suggestedFunnels,
    propertyId,
    window: suggestionDateRange,
    filterFields: filterFields as Record<string, string | undefined>,
    enabled: showLibrary && suggestedFunnels.length > 0,
  });
  const { state: urlState } = useURLState();
  const { token: authToken } = useAuth();
  const previousPeriod = usePreviousPeriodMetrics({
    propertyIds: propertyId ? [propertyId] : [],
    period: urlState.period || "7d",
    filters: urlState.filters,
    token: authToken,
    enabled: showLibrary,
  });

  const library = useMemo(() => {
    const site = domain ? String(domain).replace(/^https?:\/\//, "").replace(/^www\./, "").replace(/\/$/, "") : "";
    const range = formatRangeLabel(suggestionDateRange.start, suggestionDateRange.end);
    const rows: FunnelLibraryRow[] = funnels.map((f: any) => {
      const id = getFunnelId(f) ?? f.name;
      return {
        id,
        name: f.name || DEFAULT_FUNNEL_NAME,
        chips: funnelChips(f),
        result: funnelResults.byId.get(id) ?? null,
        updated: formatShortDate(savedFunnelUpdatedAt(f)),
      };
    });
    const answer = pickFunnelAnswer(rows);
    const suggestedRows: FunnelLibraryRow[] = suggestedFunnels.map((sf) => ({
      id: sf.id,
      name: sf.name,
      chips: funnelChips(sf),
      result: suggestedResults.byId.get(sf.id) ?? null,
      resultLoading: suggestedResults.loadingIds.has(sf.id),
      updated: null,
      suggested: true,
    }));
    const totalSteps = funnels.reduce((sum: number, f: any) => sum + (f.steps?.length || 0), 0);

    const visitors = getPeriodUniqueVisitorTotal(dashboardData?.metrics);
    const delta = visitors > 0 ? computePeriodDelta(visitors, previousPeriod.data?.visitors) : null;
    const prevRange = previousPeriod.window
      ? formatRangeLabel(previousPeriod.window.start, previousPeriod.window.end, { compact: true })
      : null;

    const clicks = (baseSuggestions?.data?.clicks ?? []).reduce((s, c) => s + (c.event_count || 0), 0);
    const forms = (baseSuggestions?.data?.form_submissions ?? []).reduce((s, c) => s + (c.event_count || 0), 0);

    const savedSub = funnels.length > 0
      ? `${totalSteps} ${totalSteps === 1 ? "step" : "steps"} in total`
      : funnelsUsage.isUnlimited
        ? `Unlimited on the ${displayPlanName} plan`
        : funnelsUsage.limit > 0
          ? `Up to ${funnelsUsage.limit} on the ${displayPlanName} plan`
          : null;

    return {
      title: funnels.length === 0
        ? suggestedRows.length > 0
          ? `${suggestedRows.length} ${suggestedRows.length === 1 ? "funnel" : "funnels"} built from your traffic, ready to save.`
          : `No funnels saved${site ? ` for ${site}` : ""} yet.`
        : answer?.headline ?? "Funnels",
      subtitle: funnels.length === 0
        ? [site, range].filter(Boolean).join(", ") +
          (suggestedRows.length > 0 ? " · open one to edit and save it, or start your own" : " · the data to build one is already here")
        : `${funnels.length} saved ${funnels.length === 1 ? "funnel" : "funnels"}${site ? ` on ${site}` : ""}${range ? `, ${range}` : ""}`,
      tiles: [
        { label: "Saved funnels", value: String(funnels.length), sub: savedSub },
        {
          label: "Site visitors",
          value: visitors > 0 ? formatNumeric(visitors) : null,
          delta: delta != null ? formatSignedPercent(delta) : null,
          sub: `Whole site, filters applied${delta != null && prevRange ? ` · vs ${prevRange}` : ""}`,
        },
        {
          label: "Tracked events",
          value: clicks + forms > 0 ? (clicks + forms).toLocaleString("en-US") : null,
          sub: clicks + forms > 0
            ? `${clicks.toLocaleString("en-US")} clicks · ${forms.toLocaleString("en-US")} form submissions`
            : null,
        },
      ] as [FunnelLibraryTile, FunnelLibraryTile, FunnelLibraryTile],
      rows: [
        ...rows.map((r) => ({
          ...r,
          resultLoading: funnelResults.loadingIds.has(r.id),
          isAnswer: answer?.id === r.id,
        })),
        ...suggestedRows,
      ],
    };
  }, [
    domain, suggestionDateRange, funnels, funnelResults, previousPeriod.data, previousPeriod.window,
    dashboardData, baseSuggestions, funnelsUsage.isUnlimited, funnelsUsage.limit, displayPlanName,
    suggestedFunnels, suggestedResults,
  ]);

  const findFunnel = (id: string) => funnels.find((f: any) => (getFunnelId(f) ?? f.name) === id);

  // Library View
  if (showLibrary) {
    return (
      <TooltipProvider>
        {/* Paper: the one orange "New funnel" sits in the shell header. */}
        <ShellHeaderActions>
          <CreateOrUpgradeButton
            size="sm"
            className="h-8 gap-1.5"
            atLimit={!funnelsUsage.canAdd}
            onClick={handleNew}
            noun="funnels"
            limit={funnelsUsage.limit}
            planName={displayPlanName}
          >
            <HugeiconsIcon icon={Add01Icon} size={12} />
            New funnel
          </CreateOrUpgradeButton>
        </ShellHeaderActions>

        <div className="w-full">
        <FunnelLibraryView
          loading={funnelsLoading}
          title={library.title}
          subtitle={library.subtitle}
          tiles={library.tiles}
          rows={library.rows}
          onOpen={(id) => {
            if (isSuggestedFunnelId(id)) {
              const sf = suggestedFunnels.find((f) => f.id === id);
              if (sf) loadSuggestedFunnel(sf);
              return;
            }
            const f = findFunnel(id);
            if (f) loadFunnel(f);
          }}
          onDelete={(id) => {
            if (isSuggestedFunnelId(id)) return;
            const f = findFunnel(id);
            if (f) setFunnelToDelete(f);
          }}
          empty={
            <FunnelLibraryEmpty
              actions={
                <CreateOrUpgradeButton
                  variant="outline"
                  size="sm"
                  className="h-8 px-3 text-[13px]"
                  atLimit={!funnelsUsage.canAdd}
                  onClick={handleNew}
                  noun="funnels"
                  limit={funnelsUsage.limit}
                  planName={displayPlanName}
                >
                  New funnel
                </CreateOrUpgradeButton>
              }
            />
          }
        >
        {/* Plan-limit banner — same pattern as heatmaps: clear upgrade path
            instead of a silent no-op / raw 402 on save. */}
        {!funnelsUsage.canAdd && !funnelLimitBannerDismissed && (
          <PlanLimitBanner
            title={funnelLimitCopy.title}
            description={funnelLimitCopy.description}
            noun="funnels"
            planName={displayPlanName}
            onDismiss={() => setFunnelLimitBannerDismissed(true)}
            isOwner={planUsageMetadata?.isOwner ?? true}
          />
        )}
        </FunnelLibraryView>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!funnelToDelete}
          onOpenChange={() => setFunnelToDelete(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this funnel?</AlertDialogTitle>
              <AlertDialogDescription>
                "{funnelToDelete?.name}" and its saved steps are removed for good. Your tracked
                data stays, so you can rebuild it any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep funnel</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={handleDeleteFunnel}>
                Delete funnel
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        </div>
      </TooltipProvider>
    );
  }

  // Studio View (Paper "Funnels · New funnel"): steps on the left, live preview
  // on the right, both raised cards in one well. Cancel + the one orange
  // "Save funnel" live in the shell header.
  const previewRange = formatRangeLabel(suggestionDateRange.start, suggestionDateRange.end);
  const conversionSoFar = runFunnel.data?.summary.overall_conversion_rate;
  const quickTargetIndex = typeof activeStepIndex === "number" ? activeStepIndex : null;
  const quickSuggestions = quickTargetIndex != null
    ? getSuggestionsForStep(steps[quickTargetIndex]?.propertyId)
    : allSuggestions;
  const quickGroups = [
    { label: "Forms", type: "form_submission" as const, items: quickSuggestions.filter((s) => s.type === "form_submission").slice(0, 4) },
    { label: "Pages", type: "page_view" as const, items: quickSuggestions.filter((s) => s.type === "page_view").slice(0, 5) },
    { label: "Clicks", type: "click" as const, items: quickSuggestions.filter((s) => s.type === "click").slice(0, 4) },
  ].filter((g) => g.items.length > 0);
  const segmentItem = (on: boolean) =>
    cn(
      "inline-flex h-[26px] items-center rounded-md px-2.5 text-xs leading-4 transition-colors",
      on
        ? "bg-card font-medium text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_1px_2px_rgb(0_0_0/0.04)]"
        : "text-foreground/60 hover:text-foreground",
    );

  return (
    <TooltipProvider>
      <ShellHeaderCrumb>
        <span className="truncate text-[13px] font-medium leading-4 text-foreground">
          {editingFunnelId ? "Edit funnel" : "New funnel"}
        </span>
      </ShellHeaderCrumb>
      <ShellHeaderActions>
        <Button variant="outline" size="sm" onClick={handleBackToLibrary} className="h-8 border-input bg-background px-3">
          Cancel
        </Button>
        <Button size="sm" onClick={handleSave} disabled={isSaving} className="h-8 px-3">
          {isSaving ? "Saving..." : "Save funnel"}
        </Button>
      </ShellHeaderActions>

      <div className="flex w-full flex-col gap-5">
        <div className="group/title flex min-w-0 items-center gap-1.5">
          <Input
            value={funnelName}
            onChange={(e) => setFunnelName(e.target.value)}
            onFocus={(e) => {
              if (e.currentTarget.value === DEFAULT_FUNNEL_NAME) {
                e.currentTarget.select();
              }
            }}
            onMouseUp={(e) => {
              if (e.currentTarget.value !== DEFAULT_FUNNEL_NAME) return;
              // mouseup after click-focus places the caret and collapses select()
              e.preventDefault();
              e.currentTarget.select();
            }}
            placeholder="Name this funnel"
            aria-label="Funnel name"
            data-testid="funnel-name"
            style={{ width: `${Math.max(funnelName.length, 8) + 1}ch` }}
            className={cn(
              "-ml-2 h-10 max-w-full min-w-0 px-2 text-[22px] font-semibold leading-7 tracking-[-0.02em] md:text-[22px]",
              "rounded-lg border-transparent bg-transparent shadow-none dark:bg-transparent",
              "placeholder:text-foreground/35 hover:bg-muted/60 focus-visible:border-input focus-visible:bg-background focus-visible:ring-0",
              funnelName.trim() === DEFAULT_FUNNEL_NAME && "text-foreground/45",
            )}
          />
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden className="shrink-0 text-foreground/35">
            <path d="M4 20h4L19 9l-4-4L4 16v4Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
          </svg>
        </div>

        <div className="well grid grid-cols-1 items-start gap-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          {/* ── Steps ── */}
          <section className="surface-raised flex min-w-0 flex-col overflow-visible">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
              <div className="flex min-w-0 flex-col gap-0.5">
                <h2 className="text-sm font-semibold leading-[18px]">Steps</h2>
                <p className="text-xs leading-4 text-foreground/60">
                  {mode === "sequential"
                    ? "Visitors must hit each step in this order."
                    : "Steps can happen in any order in one session."}
                </p>
              </div>
              <div role="radiogroup" aria-label="Funnel mode" className="flex rounded-lg bg-muted p-[3px]">
                {([["sequential", "Sequential"], ["unbounded", "Any order"]] as [FunnelMode, string][]).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={mode === value}
                    onClick={() => setMode(value)}
                    className={segmentItem(mode === value)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-2 p-4 sm:px-5">
              {steps.map((step, index) => {
                const normalizedPath = step.path.trim();
                const isActive = activeStepIndex === index;
                const stepSuggestions = getSuggestionsForStep(step.propertyId, isActive);

                // Matched (any-way, exact from the funnel engine) vs in-funnel (via the path).
                // off-path = the gap — the core fix.
                const { sessions: matchedSessions, events: matchedEventsRaw } = normalizedPath ? matchedForStep(step) : { sessions: 0, events: 0 };
                const matched = matchedSessions;
                const matchedEvents = matchedEventsRaw > 0 ? matchedEventsRaw : null;
                const stepResult = resultByStepIndex.get(index);
                const inFunnel = stepResult?.total_sessions ?? null;
                const totalEvents = stepResult?.total_events ?? null;
                const offPath = inFunnel != null && matched > 0 ? Math.max(matched - inFunnel, 0) : 0;
                const offPathEvents = matchedEvents != null && totalEvents != null ? Math.max(matchedEvents - totalEvents, 0) : null;
                const bigGap = offPath > 50 && inFunnel != null && matched > 0 && inFunnel / matched < 0.6;
                const prevStep = index > 0 ? steps[index - 1] : undefined;
                const crosses = Boolean(prevStep) && enabledProperties.length > 1 && effProp(step) !== effProp(prevStep!);
                const hasCounts = inFunnel != null || matched > 0;

                return (
                  <motion.div
                    key={index}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.15, delay: index * 0.03 }}
                    className={cn(
                      "group relative flex flex-col gap-2 overflow-visible rounded-xl p-2.5 transition-[box-shadow,background-color]",
                      // Selected = 1px --ring edge plus a soft lift, never a black ring (DESIGN.md).
                      isActive
                        ? "bg-card shadow-[0_0_0_1px_var(--ring),0_1px_3px_rgb(0_0_0/0.05)]"
                        : "bg-surface shadow-[0_0_0_1px_var(--border)]",
                    )}
                  >
                    <div className="flex min-w-0 items-center gap-2.5 overflow-visible">
                      <span
                        className={cn(
                          "flex size-[22px] shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums",
                          normalizedPath && !isActive
                            ? "bg-muted text-foreground shadow-[inset_0_0_0_1px_var(--border)]"
                            : "bg-card text-foreground shadow-[inset_0_0_0_1px_var(--ring)]",
                        )}
                      >
                        {index + 1}
                      </span>
                      {enabledProperties.length > 1 && (
                        <Select
                          value={step.propertyId || propertyId || ""}
                          onValueChange={(v) => updateStep(index, { propertyId: v, path: "", secondary: undefined })}
                        >
                          <SelectTrigger className="h-9 w-auto max-w-[130px] shrink-0 gap-1.5 rounded-lg bg-background px-2 text-xs font-medium">
                            <SelectValue>
                              <span className="truncate text-xs">
                                {(() => {
                                  const pid = step.propertyId || propertyId;
                                  const prop = enabledProperties.find((p) => p.propertyId === pid);
                                  return prop?.domain || prop?.name || "Primary";
                                })()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {enabledProperties.map((p) => (
                              <SelectItem key={p.propertyId} value={p.propertyId} className="text-xs">
                                {p.domain || p.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <StepConditionInput
                        step={step}
                        onChange={(updates) => updateStep(index, updates)}
                        suggestions={stepSuggestions}
                        onFocus={() => setActiveStepIndex(index)}
                        onSearchChange={setActiveSearch}
                        isActive={isActive}
                      />
                      {steps.length > 2 && (
                        <button
                          type="button"
                          aria-label={`Remove step ${index + 1}`}
                          onClick={() => removeStep(index)}
                          className="flex size-7 shrink-0 items-center justify-center rounded-md text-foreground/0 transition-colors group-hover:text-foreground/40 hover:bg-danger-tint hover:!text-danger focus-visible:text-foreground/60"
                        >
                          <svg className="size-3.5" viewBox="0 0 14 14" fill="none"><path d="M4 4l6 6M10 4l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        </button>
                      )}
                    </div>
                    {(hasCounts || crosses || (isActive && bigGap)) && (
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-8 text-xs leading-4 text-foreground/60 tabular-nums">
                        {hasCounts && (
                          <span
                            title={showTotals
                              ? "Total events fired at this step by visitors who came through the steps above (one person can trigger multiple events)"
                              : "Unique visitors who reached this step by going through each step above, in order"}
                            className="cursor-help"
                          >
                            <b className="font-medium text-foreground">
                              {formatNumeric(showTotals ? (totalEvents ?? inFunnel ?? matched) : (inFunnel ?? matched))}
                            </b>{" "}
                            {inFunnel != null ? "in path" : METRIC.visitor.plural}
                          </span>
                        )}
                        {offPath > 0 && (
                          <span
                            className="cursor-help"
                            title={showTotals
                              ? "Total events fired at this step by visitors who skipped the steps above. They arrived here directly (e.g. from an ad, bookmark, or different page)"
                              : "Unique visitors who reached this step without going through the steps above. They arrived here directly (e.g. from an ad, bookmark, or different page)"}
                          >
                            · {formatNumeric(showTotals ? (offPathEvents ?? offPath) : offPath)} off path
                          </span>
                        )}
                        {hasCounts && (totalEvents != null || matchedEvents != null) && (
                          <button
                            type="button"
                            onClick={() => setShowTotals((v) => !v)}
                            className="text-foreground/45 underline underline-offset-2 transition-colors hover:text-foreground"
                            title={showTotals
                              ? "Currently showing total event count. Click to switch to unique visitors"
                              : "Currently showing unique visitors. Click to switch to total event count (one person can trigger multiple events)"}
                          >
                            {showTotals ? "Show uniques" : "Show totals"}
                          </button>
                        )}
                        {crosses && (
                          <span
                            className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 font-medium text-foreground/70"
                            title={`Crosses from ${domainForStep(prevStep!) || "previous domain"} to ${domainForStep(step) || "this domain"}. Cross-domain tracking links the two sessions.`}
                          >
                            <HugeiconsIcon icon={ArrowUpRight01Icon} size={11} />
                            Cross-domain
                          </span>
                        )}
                        {isActive && bigGap && (
                          <span className="basis-full">
                            Many visitors reach <Pattern value={step.path} /> directly (ads, bookmarks) without this funnel.
                          </span>
                        )}
                      </div>
                    )}
                  </motion.div>
                );
              })}

              {steps.length < STEP_CAP ? (
                <button
                  type="button"
                  onClick={addStep}
                  className="flex h-9 items-center justify-center gap-1.5 rounded-lg bg-card text-xs font-medium text-foreground/80 shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-muted/60 hover:text-foreground"
                >
                  <HugeiconsIcon icon={Add01Icon} size={12} />
                  Add step
                </button>
              ) : (
                <p className="flex items-center gap-2 px-1 text-xs text-foreground/60">
                  <HugeiconsIcon icon={InformationCircleIcon} size={13} />
                  Maximum {STEP_CAP} steps. The vertical layout keeps funnels this long legible.
                </p>
              )}
            </div>

            {quickGroups.length > 0 && (
              <div className="flex flex-col gap-3 border-t border-border px-4 pb-5 pt-4 sm:px-5">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-[13px] font-semibold leading-4">Quick picks</h3>
                  <span className="text-xs leading-4 text-foreground/60">
                    {quickTargetIndex != null ? `Fills step ${quickTargetIndex + 1} · ` : "Pick a step first · "}
                    wildcards supported
                  </span>
                </div>
                {quickGroups.map((group) => (
                  <div key={group.label} className="flex flex-col gap-2">
                    <span className="text-xs leading-4 text-foreground/60">{group.label}</span>
                    <div className="flex flex-wrap gap-1.5">
                      {group.items.map((item) => (
                        <button
                          key={`${item.value}::${item.secondary || ""}`}
                          type="button"
                          onClick={() => {
                            if (quickTargetIndex != null) {
                              updateStep(quickTargetIndex, { path: item.value, type: group.type, secondary: group.type === "page_view" ? undefined : item.secondary });
                            } else if (group.type === "page_view") {
                              handleSuggestionApply(item.value);
                            }
                          }}
                          className="inline-flex h-[30px] max-w-full items-center gap-2 rounded-lg bg-card px-2.5 text-[13px] leading-4 text-foreground shadow-[0_0_0_1px_var(--input)] transition-colors hover:bg-muted/60"
                        >
                          <span className="truncate max-w-[160px]">{group.type === "page_view" ? item.value : isGenericTarget(item.value) ? item.label : item.secondary || item.value}</span>
                          {item.sessions > 0 && <span className="text-xs text-foreground/50 tabular-nums">{formatNumeric(item.sessions)}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* ── Preview ── */}
          <section className="surface-raised flex min-w-0 flex-col lg:sticky lg:top-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3 sm:px-5">
              <h2 className="flex items-center gap-2 text-sm font-semibold leading-[18px]">
                Preview
                {isLoading && <HugeiconsIcon icon={Loading01Icon} size={13} className="animate-spin text-foreground/50" />}
              </h2>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="cursor-help text-xs leading-4 text-foreground/60 hover:text-foreground">
                    Counts are unique visitors{previewRange ? ` · ${previewRange}` : ""}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end" className="max-w-[280px] text-xs leading-relaxed">
                  {FUNNEL_COUNT_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </div>

            {hasFlowResults && runFunnel.data && (
              <div className="flex flex-wrap items-center justify-end gap-2 px-4 pt-3 sm:px-5">
                {resultsView === "chart" && (
                  <div
                    className="flex rounded-lg bg-muted p-[3px]"
                    title={tooMany ? "Stream & Bars work best for ≤8 steps" : undefined}
                  >
                    {([
                      ["steps", "Steps"],
                      ["stream", "Stream"],
                      ["bars", "Bars"],
                      ["list", "Vertical"],
                    ] as [FunnelChartStyle, string][]).map(([key, label]) => {
                      const disabled = tooMany && key !== "list" && key !== "steps";
                      return (
                        <button
                          key={key}
                          type="button"
                          disabled={disabled}
                          onClick={() => setChartStyle(key)}
                          className={cn(segmentItem(effChartStyle === key), disabled && "cursor-not-allowed opacity-40")}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="flex rounded-lg bg-muted p-[3px]">
                  {(["chart", "table"] as ResultsView[]).map((v) => (
                    <button key={v} type="button" onClick={() => setResultsView(v)} className={segmentItem(resultsView === v)}>
                      {v === "chart" ? "Chart" : "Table"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={cn("px-4 py-5 sm:px-5", !hasFlowResults && "min-h-[160px]")}>
            <AnimatePresence mode="wait">
              {Boolean(error) && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-10 text-center">
                  <p className="mb-2 text-sm font-medium text-danger">Analysis failed</p>
                  <p className="text-xs text-muted-foreground">{errorMessage}</p>
                </motion.div>
              )}
              {!hasValidSteps && !error && (
                <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col gap-2">
                  {steps.map((step, i) => (
                    <div
                      key={i}
                      className="flex h-11 items-center gap-2.5 rounded-xl bg-surface px-3 text-[13px] leading-4 text-foreground/60 shadow-[0_0_0_1px_var(--border)]"
                    >
                      <span className="flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-foreground/60 shadow-[inset_0_0_0_1.5px_currentColor]">
                        {i + 1}
                      </span>
                      {step.path.trim()
                        ? <span className="truncate text-foreground">{step.secondary || step.path}</span>
                        : `Pick a ${STEP_TYPE_CONFIG[step.type as StepType]?.shortLabel.toLowerCase() ?? "target"} to see step ${i + 1}`}
                    </div>
                  ))}
                  <p className="pt-1 text-xs leading-4 text-foreground/50">The preview fills in once two steps have a target.</p>
                </motion.div>
              )}
              {hasValidSteps && isLoading && !hasFlowResults && (
                <motion.div key="flow-loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <LoadingState message="Analyzing funnel..." />
                </motion.div>
              )}
              {hasFlowResults && runFunnel.data && resultsView === "chart" && (
                <motion.div key="flow-chart" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  {effChartStyle === "stream" ? (
                    <FunnelFlowChart data={runFunnel.data} stepTypes={validStepTypes} height={280} />
                  ) : (
                    <FunnelChartView steps={chartSteps} entered={chartSteps[0]?.inFunnel || 0} style={effChartStyle} />
                  )}
                  {tooMany && effChartStyle === "list" && (
                    <p className="mt-2.5 text-center text-[11px] text-muted-foreground">
                      <HugeiconsIcon icon={InformationCircleIcon} size={12} className="mr-1 inline -translate-y-px" />
                      {validStepCount} steps, so we switched to the vertical layout, which stays legible for long funnels.
                    </p>
                  )}
                </motion.div>
              )}
              {hasFlowResults && runFunnel.data && resultsView === "table" && (
                <motion.div key="flow-table" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 pb-3 border-b border-border/20 overflow-x-auto scrollbar-hide">
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Entered</span>
                      <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumeric(runFunnel.data.summary.total_entered)}</span>
                    </div>
                    <div className="w-px h-3 bg-border/30 shrink-0" />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Completed</span>
                      <span className="text-sm font-semibold text-foreground tabular-nums">{formatNumeric(runFunnel.data.summary.total_completed)}</span>
                    </div>
                    <div className="w-px h-3 bg-border/30 shrink-0" />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Conversion</span>
                      <span className="text-sm font-semibold text-primary tabular-nums">{Number.isFinite(runFunnel.data.summary.overall_conversion_rate) ? `${(runFunnel.data.summary.overall_conversion_rate * 100).toFixed(1)}%` : "0%"}</span>
                    </div>
                    <div className="w-px h-3 bg-border/30 shrink-0" />
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">Drop-off</span>
                      <span className="text-sm font-semibold text-primary-strong tabular-nums">{formatNumeric(runFunnel.data.summary.total_entered - runFunnel.data.summary.total_completed)}</span>
                    </div>
                  </div>
                  <div className="responsive-table-wrapper">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-border/60 hover:bg-transparent">
                          <TableHead className="data-table-th min-w-[180px]">Step</TableHead>
                          <TableHead className="data-table-th text-right min-w-[90px]">Unique in funnel</TableHead>
                          <TableHead className="data-table-th text-right min-w-[100px] hidden lg:table-cell">Total in funnel</TableHead>
                          <TableHead className="data-table-th text-right min-w-[90px] hidden lg:table-cell">Off-path</TableHead>
                          <TableHead className="data-table-th text-right min-w-[100px] hidden sm:table-cell">From previous</TableHead>
                          <TableHead className="data-table-th text-right min-w-[90px] hidden md:table-cell">Of total</TableHead>
                          <TableHead className="data-table-th text-right min-w-[90px]">Drop-off</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {runFunnel.data.data.steps.map((step: any, index: number) => {
                          const maxSessions = runFunnel.data!.data.steps[0]?.total_sessions || 1;
                          const prevSessions = index > 0 ? runFunnel.data!.data.steps[index - 1].total_sessions : step.total_sessions;
                          const fromPrevPct = index === 0 ? 100 : safeRatio(step.total_sessions, prevSessions) * 100;
                          const ofTotalPct = safeRatio(step.total_sessions, maxSessions) * 100;
                          const dropPct = index === 0 ? 0 : 100 - fromPrevPct;
                          const matchedTotal = chartSteps[index]?.matches ?? step.total_sessions;
                          const offPath = Math.max(matchedTotal - step.total_sessions, 0);
                          return (
                            <TableRow key={index} className="data-table-row">
                              <TableCell className="data-table-cell data-table-cell-primary">
                                <div className="flex items-center gap-2">
                                  <span className="w-5 h-5 rounded-full bg-muted/70 text-muted-foreground text-[11px] font-semibold flex items-center justify-center shrink-0">{index + 1}</span>
                                  <span className="truncate text-sm font-medium">{step.step_name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric"><span className="font-semibold text-foreground">{formatNumeric(step.total_sessions)}</span></TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric hidden sm:table-cell">
                                <span className="text-muted-foreground tabular-nums">{formatNumeric(step.total_events)}</span>
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric hidden lg:table-cell">
                                <span className="text-muted-foreground tabular-nums">{formatNumeric(step.total_events)}</span>
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric hidden lg:table-cell">
                                {offPath > 0 ? (
                                  <span className="text-muted-foreground tabular-nums" title="Reached this step's target without coming through the path above">+{formatNumeric(offPath)}</span>
                                ) : (
                                  <span className="text-muted-foreground/50">—</span>
                                )}
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric hidden sm:table-cell">
                                {index === 0 ? <span className="text-muted-foreground">--</span> : (
                                  <span className={cn("font-medium", fromPrevPct >= 50 ? "text-foreground" : "text-muted-foreground")}>{fromPrevPct.toFixed(1)}%</span>
                                )}
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric hidden md:table-cell">
                                <div className="flex items-center justify-end gap-2">
                                  <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden"><div className="h-full bg-chart-1/60 rounded-full" style={{ width: `${ofTotalPct}%` }} /></div>
                                  <span className="font-medium tabular-nums">{ofTotalPct.toFixed(1)}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="data-table-cell data-table-cell-numeric">
                                {index === 0 ? <span className="text-muted-foreground">--</span> : (
                                  <span className={cn("font-medium", dropPct > 50 ? "text-primary-strong" : "text-muted-foreground")}>{dropPct.toFixed(1)}%</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            {hasFlowResults && conversionSoFar != null && Number.isFinite(conversionSoFar) && (
              <div className="px-4 pb-5 sm:px-5">
                <div className="flex flex-col gap-1 rounded-xl bg-surface p-3">
                  <span className="text-xs leading-4 text-foreground/60">Conversion so far</span>
                  <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] tabular-nums">
                    {(conversionSoFar * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </TooltipProvider>
  );

}

export default function FunnelStudio() {
  return (
    <FeatureGate feature="funnels">
      <FunnelStudioInner />
    </FeatureGate>
  )
}
